import { Link2, MapPin, Plane, Repeat } from "lucide-react";
import { CAT_RGB, addDays, daysOf, dayOf, fmtTime, parseYmd, whenLabel, ymd } from "@/lib/calendar";

const tidOf = (e) => e.id.replace(/[^a-zA-Z0-9]/g, "-");

export function EventChip({ e, onOpen, compact }) {
  const rgb = CAT_RGB[e.category];
  return (
    <button onClick={() => onOpen(e)} data-testid={`cal-event-${tidOf(e)}`} data-title={e.title}
      className="flex w-full min-h-[44px] items-center gap-1.5 truncate rounded-md px-2 py-1 text-left text-xs font-semibold text-q-text transition-[background-color,transform] duration-150 hover:-translate-y-px"
      style={{ background: `rgba(${rgb},0.16)`, boxShadow: `inset 3px 0 0 rgb(${rgb})` }}>
      {e.source === "link" && <Link2 className="h-3 w-3 flex-shrink-0 opacity-70" data-testid={`cal-event-link-icon-${tidOf(e)}`} />}
      {e.category === "Flights" && <Plane className="h-3 w-3 flex-shrink-0 opacity-70" />}
      {!compact && !e.all_day && <span className="tabular-nums text-q-muted" data-testid={`cal-time-${tidOf(e)}`}>{fmtTime(e.start)}</span>}
      <span className="truncate">{e.title}</span>
    </button>
  );
}

function byDay(events) {
  const m = {};
  events.forEach((e) => daysOf(e).forEach((d) => { (m[d] = m[d] || []).push(e); }));
  return m;
}

export function MonthView({ anchor, events, onOpen, onDay }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = addDays(first, -first.getDay());
  const map = byDay(events);
  const today = ymd(new Date());
  return (
    <div className="q-card overflow-hidden p-0" data-testid="cal-month-view">
      <div className="grid grid-cols-7 border-b border-q-line text-center text-[10px] font-black uppercase tracking-[0.16em] text-q-faint">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: 42 }, (_, i) => {
          const d = addDays(start, i);
          const k = ymd(d);
          const list = map[k] || [];
          const out = d.getMonth() !== anchor.getMonth();
          return (
            <div key={k} className={`min-h-[112px] border-b border-r border-q-line p-1.5 ${out ? "q-out-month" : ""}`} data-testid={`cal-day-${k}`}>
              <button onClick={() => onDay(d)} className={`mb-1 inline-flex h-11 min-w-[44px] items-center justify-center rounded-full px-1 text-xs font-bold ${k === today ? "text-white" : "text-q-muted"}`}
                style={k === today ? { background: "rgb(var(--glow-calendar))" } : undefined} data-testid={`cal-day-num-${k}`}>{d.getDate()}</button>
              <div className="space-y-1">
                {list.slice(0, 3).map((e) => <EventChip key={e.id + k} e={e} onOpen={onOpen} compact />)}
                {list.length > 3 && <button onClick={() => onDay(d)} className="min-h-[44px] w-full px-2 text-left text-[11px] font-bold text-q-muted" data-testid={`cal-day-more-${k}`}>+{list.length - 3} more</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WeekView({ anchor, events, onOpen }) {
  const start = addDays(anchor, -anchor.getDay());
  const map = byDay(events);
  const today = ymd(new Date());
  return (
    <div className="grid gap-3 md:grid-cols-7" data-testid="cal-week-view">
      {Array.from({ length: 7 }, (_, i) => {
        const d = addDays(start, i);
        const k = ymd(d);
        const list = (map[k] || []).sort((a, b) => (a.all_day === b.all_day ? a.sort.localeCompare(b.sort) : a.all_day ? -1 : 1));
        return (
          <div key={k} className="q-card min-h-[160px] p-3" data-testid={`cal-week-day-${k}`} style={k === today ? { borderColor: "rgba(var(--glow-calendar),0.6)" } : undefined}>
            <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-q-faint">{d.toLocaleDateString([], { weekday: "short" })} <span className="text-q-text">{d.getDate()}</span></p>
            <div className="space-y-1.5">{list.map((e) => <EventChip key={e.id + k} e={e} onOpen={onOpen} />)}</div>
          </div>
        );
      })}
    </div>
  );
}

export function AgendaView({ events, onOpen, people }) {
  const groups = {};
  events.forEach((e) => { const k = dayOf(e); (groups[k] = groups[k] || []).push(e); });
  const keys = Object.keys(groups).sort();
  if (!keys.length) return <p className="q-card text-center text-sm text-q-muted" data-testid="cal-agenda-empty">Nothing on the calendar for these dates.</p>;
  const names = Object.fromEntries((people || []).map((p) => [p.key, p.name.split(" ")[0]]));
  return (
    <div className="space-y-6" data-testid="cal-agenda-view">
      {keys.map((k) => (
        <section key={k} data-testid={`cal-agenda-day-${k}`}>
          <h3 className="mb-2 font-display text-sm font-extrabold uppercase tracking-[0.12em] text-q-muted">{parseYmd(k).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</h3>
          <ul className="space-y-2">
            {groups[k].map((e) => (
              <li key={e.id}>
                <button onClick={() => onOpen(e)} className="q-card flex w-full items-start gap-3 p-4 text-left transition-transform duration-150 hover:-translate-y-0.5" data-testid={`cal-agenda-event-${tidOf(e)}`}>
                  <span className="mt-1 h-10 w-1.5 flex-shrink-0 rounded-full" style={{ background: `rgb(${CAT_RGB[e.category]})` }} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 font-semibold text-q-text">{e.source === "link" && <Link2 className="h-3.5 w-3.5 text-q-muted" />}{e.title}{e.repeat && <Repeat className="h-3.5 w-3.5 text-q-faint" />}</span>
                    <span className="block text-xs text-q-muted">{whenLabel(e)} · {e.category}{e.source_name ? ` · ${e.source_name}` : ""}</span>
                    {e.location && <span className="mt-0.5 flex items-center gap-1 text-xs text-q-faint"><MapPin className="h-3 w-3" />{e.location}</span>}
                    {!!e.member_keys.length && <span className="mt-0.5 block text-xs text-q-faint">{e.member_keys.map((m) => names[m]).filter(Boolean).join(", ")}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
